import React, { useState, useEffect } from "react";
import { MDialog } from "@/components/ui/atoms/MDialog";
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";

import { useNavigate } from "react-router-dom";
import {
  IconEdit,
  IconCircleX,
  IconShieldHeart,
  IconCircleMinus,
} from "@tabler/icons-react";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { useInsurance } from "@/hooks/useInsurance";
import { useToast } from "@/components/ui/atoms/MToastProvider";

import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { useNavigationSource } from '@/hooks/useNavigationSource';
import { Insurance as IInsurance } from "@/types/insurance";
import { formatPrettyDate, formatPrettyDateNoOrdinal } from "@/utils/date-utils";
import { MemberType } from "@/types/member";
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const Insurance: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const memberInfo = getMemberInfo();
  const {
    insurances,
    fetchInsurance,
    deleteInsurance,
    unlinkFamilyMember,
  } = useInsurance({ memberInfo });
  const [loading, setLoading] = useState(true);

  // State for unlink dialog
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [insuranceToUnlink, setInsuranceToUnlink] = useState<any | null>(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);

  const handleUnlink = (insurance: any) => {
    setInsuranceToUnlink(insurance);
    setShowUnlinkModal(true);
  };

  const confirmUnlink = async () => {
    if (!insuranceToUnlink) return;
    setUnlinkLoading(true);
    try {
      await unlinkFamilyMember(insuranceToUnlink, memberInfo.id);
      fetchInsurance();
      setShowUnlinkModal(false);
      setInsuranceToUnlink(null);
    } catch (err: any) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setUnlinkLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        await fetchInsurance();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (memberInfo?.id) {
      fetchData();
    }
    return () => {
      cancelled = true;
    };
  }, [memberInfo?.id, fetchInsurance]);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_INSURANCE,
    });
  }, [memberInfo?.id]);

  // Edit handler
  const handleEdit = (insurance: any) => {
    setShowDeleteModal(false);
    setInsuranceToDelete(null);
    navigate("/profile/basic-information/insurance/form", {
      state: { insurance },
    });
  };
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [insuranceToDelete, setInsuranceToDelete] = useState<any | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = (insurance: any) => {
    setInsuranceToDelete(insurance);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!insuranceToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteInsurance(insuranceToDelete.id || insuranceToDelete._id);
      fetchInsurance();
      
      // Track delete policy event
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_INSURANCE_DELETE_POLICY,
      });
      
      setShowDeleteModal(false);
      setInsuranceToDelete(null);
    } catch (err: any) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Show insurance type selection options
  const handleAdd = () => {
    if (memberInfo.memberType === MemberType.PET) {
      // For pets, go directly to private insurance form
      navigate("/profile/basic-information/insurance/form", {
        state: { insuranceType: "private" },
      });
      return;
    }
    // Navigate to the new add insurance page
    navigate(`/profile/basic-information/insurance/add?count=${insurances.length}`);
  };

  const getActions = (insurance: IInsurance) => {
    const actions = [];
    if (insurance.memberId === memberInfo.id) {
      actions.push({
        id: "edit",
        label: "Edit",
        icon: <IconEdit size={20} />,
        onClick: () => handleEdit(insurance),
      });
      actions.push({
        id: "delete",
        label: "Delete",
        icon: <IconCircleX size={20} />,
        onClick: () => handleDelete(insurance),
        className: "delete",
      });
    }
    // Add Unlink if canUnlink
    const canUnlink =
      insurance.coveredMembers &&
      Array.isArray(insurance.coveredMembers) &&
      insurance.coveredMembers.includes(memberInfo.id) &&
      insurance.memberId !== memberInfo.id;
    if (canUnlink) {
      actions.push({
        id: "unlink",
        label: "Unlink",
        icon: <IconCircleMinus size={20} />,
        onClick: () => handleUnlink(insurance),
        className: "delete", // style as destructive
      });
    }
    return actions;
  };

  if (loading) {
    return <MLoader />;
  }

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Insurance"
        title=""
        subHeading="Health insurance"
        memberInfo={memberInfo}
        onAdd={handleAdd}
        showGradient={true}
        showProfilePickerRow={true}
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/basic-information");
          }
        }}
      />

      {/* Empty state for no insurance */}
      {insurances && insurances.length === 0 && (
        <>
          <MSpacer />
          <MEmptyCard
            icon={<IconShieldHeart />}
            primaryTitle={t("insurance.noInsurance")}
            primaryCTA={{
              text: t("insurance.addPolicy"),
              onClick: handleAdd,
            }}
          />
        </>
      )}

      {/* List of insurances */}
      {insurances && insurances.length > 0 && (
        <>
          <div className="profile-flex-col-gap">
            {insurances.map((insurance: any) => {
              const validUntilText = insurance.isLifetimePolicy
                ? t("insurance.validUntil", { date: t("insurance.lifetime") })
                : t("insurance.validUntil", {
                    date: formatPrettyDateNoOrdinal(
                      insurance.policyEndDate || insurance.validUntil
                    ),
                  });
              
              return (
                <ListItem
                  key={insurance._id}
                  type="2-line"
                  variant="purple"
                  title={insurance.insurerName || insurance.provider}
                  secondaryText={t("insurance.primaryHolder", {
                    name:
                      insurance.primaryPolicyHolderName ||
                      insurance.primaryPolicyHolder,
                  })}
                  tertiaryText={validUntilText}
                  actions={getActions(insurance)}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Render MDialog for delete confirmation */}
      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setInsuranceToDelete(null);
        }}
        title={t("insurance.deletePolicy")}
        description={t("insurance.deletePolicyDescription", {
          name: insuranceToDelete?.insurerName || insuranceToDelete?.provider,
        })}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={confirmDelete}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setInsuranceToDelete(null);
        }}
        isPrimaryCtaLoading={deleteLoading}
      />
      {/* Render MDialog for unlink confirmation */}
      <MDialog
        isOpen={showUnlinkModal}
        onClose={() => {
          setShowUnlinkModal(false);
          setInsuranceToUnlink(null);
        }}
        title={t("insurance.unlinkPolicy")}
        description={t("insurance.unlinkPolicyDescription")}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={confirmUnlink}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowUnlinkModal(false);
          setInsuranceToUnlink(null);
        }}
        isPrimaryCtaLoading={unlinkLoading}
      />
    </div>
  );
};

export default Insurance;
